import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MdAdd, MdLocalShipping } from 'react-icons/md'
import type { Subscription, LineAmendment, LineFreeze, SubscriptionLine } from '@/types'
import type { HolidayRange } from '@/utils/holidayRanges'
import { getDayName } from '@/utils/deliveryDays'
import { stripEmoji } from '@/utils/emoji'
import * as weeklyOrder from '@/utils/weeklyOrder'
import { frequencyTag } from '@/utils/billingFrequency'
import DailyDeliveryCard from './DailyDeliveryCard'
import SkippedDeliveriesPanel from './SkippedDeliveriesPanel'
import OneOffDeliveriesBox from './OneOffDeliveriesBox'
import '@/styles/components/delivery-management.css'

const DRIVER_NOTE_MAX = 140

/** Monday-first, with Sunday at the end of the week. */
const orderDays = (days: number[]): number[] =>
  [...days].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))

/** "Tuesdays and Fridays", "Mondays, Wednesdays and Fridays". Plural, because
 *  it describes the recurring round rather than one delivery. */
const listDays = (days: number[]): string => {
  const names = orderDays(days).map(d => `${getDayName(d)}s`)
  if (names.length < 2) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}


/**
 * The delivery address, once there is somewhere to read it from. Until the
 * backend exposes it (Chargebee billing/shipping address), the block renders a
 * placeholder so the layout can be reviewed, and Update is disabled.
 */
export interface DeliveryAddress {
  lines: string[]
  city?: string | null
  postcode?: string | null
}

interface Props {
  subscriptions: Subscription[]
  amendments: LineAmendment[]
  /** Temporary product pauses (Line Freezes, DF-350). */
  freezes?: LineFreeze[]
  holidayRanges: HolidayRange[]
  /**
   * The round's delivery days (from the customer's CRM record), which is what
   * "we deliver in your area on..." should say. It is broader than the days
   * that currently carry a product, so a customer taking milk only on Fridays
   * still sees the Tuesday their round runs.
   */
  deliveryDays?: number[]
  loading?: boolean
  editingDisabled?: boolean

  onAddProduct: () => void
  onViewProduct: (productId: string, name: string, unitPrice: number) => void
  /** Change a product's quantity on one day; opens the "when" dialog. */
  onChangeQuantity: (productId: string, day: number, newQty: number) => void
  /** Pause a product; opens the "when" dialog. */
  onPause: (productId: string) => void
  /** Remove a product; opens the "when" dialog. */
  onDelete: (productId: string) => void
  /** Undo a scheduled change; opens the confirm dialog. */
  onCancelChange: (change: weeklyOrder.ScheduledChange) => void

  /** Open the holiday calendar (adding and editing happen there together). */
  onManageHolidays: () => void

  /** Null until the backend provides it; the block shows a placeholder. */
  address?: DeliveryAddress | null
  onUpdateAddress?: () => void

  /** Not yet persisted; the field is live but Save is a placeholder for now. */
  driverNote?: string
  onSaveDriverNote?: (note: string) => void

  /** One-off product lines (milk lockers, DF-230), listed in their own box at
   *  the bottom, separate from the day-by-day products. */
  oneOffLines?: SubscriptionLine[]
  /** Remove a one-off; opens the same remove dialog as a normal product. */
  onRemoveOneOff?: (productId: string) => void
  /** Move a one-off to another delivery day (same cut-off rules as remove). */
  onMoveOneOff?: (productId: string) => void
}

/**
 * Delivery Management: what a customer gets, when, and where.
 *
 * Two columns, mirroring the My Deliveries layout: the deliveries themselves
 * take the wide left column, and the supporting detail (address, paused
 * deliveries, driver note) sits in the right rail.
 *
 * The deliveries are shown as the same product cards as the edit-your-delivery
 * dialog's first step: each product with its standing order and any scheduled
 * changes, and an Edit that opens that dialog on the product. Lifted from the
 * dialog so the summary and the editor cannot drift apart.
 */
const DeliveryManagementPanel: React.FC<Props> = ({
  subscriptions,
  amendments,
  freezes = [],
  holidayRanges,
  deliveryDays = [],
  loading = false,
  editingDisabled = false,
  onAddProduct,
  onViewProduct,
  onChangeQuantity,
  onPause,
  onDelete,
  onCancelChange,
  onManageHolidays,
  address = null,
  driverNote = '',
  onSaveDriverNote,
  oneOffLines = [],
  onRemoveOneOff,
  onMoveOneOff,
}) => {
  const relevant = useMemo(() => weeklyOrder.relevantLines(subscriptions), [subscriptions])
  const days = useMemo(() => weeklyOrder.deliveryDays(relevant), [relevant])
  const rows = useMemo(() => weeklyOrder.rows(relevant, amendments), [relevant, amendments])
  const changes = useMemo(() => weeklyOrder.changesByProduct(rows, amendments, freezes), [rows, amendments, freezes])

  /** Each subscription's cadence tag, keyed by external_id, so a product can
   *  show the frequency of the subscription its line belongs to. */
  const frequencyBySubscription = useMemo(() => {
    const map = new Map<string, string>()
    subscriptions.forEach(s => {
      const label = frequencyTag(s.billing_frequency)
      if (label) map.set(s.external_id, label)
    })
    return map
  }, [subscriptions])

  /** A product's cadence, from the subscription its governing line sits on. */
  const frequencyForRow = (row: weeklyOrder.OrderRow): string | null => {
    const line = Array.from(row.linesByDay.values())[0]
    return line ? frequencyBySubscription.get(line.subscription_external_id) ?? null : null
  }

  const [note, setNote] = useState(driverNote)

  // Re-seed when the saved note loads or changes (e.g. after a save + refresh).
  // driverNote only changes on load or save, never mid-edit, so this cannot
  // clobber what the customer is typing.
  useEffect(() => { setNote(driverNote) }, [driverNote])

  // Emojis break the CRM sync (DF-349), so they are kept out of the note as it
  // is typed or pasted rather than stripped on save. A brief hint shows when one
  // is blocked, so the field doesn't just feel unresponsive.
  const [emojiHint, setEmojiHint] = useState(false)
  const emojiHintTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(emojiHintTimer.current), [])

  const handleNoteChange = (raw: string) => {
    const clean = stripEmoji(raw)
    if (clean !== raw) {
      setEmojiHint(true)
      window.clearTimeout(emojiHintTimer.current)
      emojiHintTimer.current = window.setTimeout(() => setEmojiHint(false), 3000)
    }
    setNote(clean)
  }

  /**
   * The scheduled changes for a product on one day. A quantity amendment or an
   * ending belongs to its own day, so it is filtered to that day now the same
   * product can appear under two. A starting or a pause acts on the whole
   * product, so it shows on every day it is delivered.
   */
  const changesForDay = (row: weeklyOrder.OrderRow, day: number): weeklyOrder.ScheduledChange[] =>
    (changes.get(row.productId) ?? [])
      .filter(c => c.kind === 'starting' || c.kind === 'pause' || c.kind === 'freeze' || c.day === day)

  return (
    <>
      {/* Left column: the deliveries themselves ---------------------- */}
      <div className="sun-column dm">
        <section className="sun-panel dm-deliveries">
          <div className="sun-card-head">
            <h2 className="sun-card-title">Your regular delivery</h2>
            <button
              className="sun-btn sun-btn-add"
              onClick={onAddProduct}
              disabled={editingDisabled}
            >
              <MdAdd aria-hidden="true" /> Add a product
            </button>
          </div>

          {loading ? (
            <div className="sun-loading" role="status">
              <div className="spinner" aria-hidden="true" />
              <p>Loading your deliveries...</p>
            </div>
          ) : rows.length === 0 ? (
            <p className="sun-empty">You have no products in your order yet.</p>
          ) : (
            <div className="dm-days-list">
              {days.map(day => (
                <div key={day} className="dm-day-group">
                  <h3 className="dm-day-name">{getDayName(day)}</h3>
                  <div className="dm-day-cards">
                    {rows.filter(row => row.linesByDay.has(day)).map(row => (
                      <DailyDeliveryCard
                        key={row.productId}
                        name={row.name}
                        image={row.image}
                        // Price this day from its own line: a product can cost
                        // different amounts on different days (old vs new/uplifted
                        // line), so this must match My Deliveries per day (DF-242).
                        unitPrice={row.unitPriceByDay.get(day) ?? row.unitPrice}
                        frequency={frequencyForRow(row)}
                        standingQty={row.quantityByDay.get(day) ?? 0}
                        changes={changesForDay(row, day)}
                        onCancelChange={onCancelChange}
                        disabled={editingDisabled}
                        onViewDetail={() => onViewProduct(row.productId, row.name, row.unitPriceByDay.get(day) ?? row.unitPrice)}
                        onChangeQuantity={(newQty) => onChangeQuantity(row.productId, day, newQty)}
                        onPause={() => onPause(row.productId)}
                        onDelete={() => onDelete(row.productId)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Skipped deliveries sit under the order on the left. */}
        <SkippedDeliveriesPanel
          ranges={holidayRanges}
          loading={loading}
          disabled={editingDisabled}
          onManage={onManageHolidays}
        />

        {/* One-off products (milk lockers, DF-230) in their own box at the
            bottom, separate from the day-by-day order above. */}
        <OneOffDeliveriesBox
          lines={oneOffLines}
          variant="management"
          onRemove={onRemoveOneOff}
          onMove={onMoveOneOff}
          removeDisabled={editingDisabled}
          moveDisabled={editingDisabled}
        />
      </div>

      {/* Right rail: address, driver note ---------------------------- */}
      <div className="sun-column sun-rail dm">
        {/* Delivering to */}
        <section className="sun-panel dm-address">
          {/* Icon and Update button removed for now; to be added back with the
              CRM write path. */}
          <h2 className="sun-card-title">Delivering to</h2>

          {address ? (
            <address className="dm-address-body">
              {address.lines.map((line, i) => <span key={i}>{line}</span>)}
              {address.city && <span>{address.city}</span>}
              {address.postcode && <span>{address.postcode}</span>}
            </address>
          ) : (
            <p className="dm-placeholder">
              Your delivery address will appear here.
            </p>
          )}

          {(deliveryDays.length > 0 || days.length > 0) && (
            <p className="dm-days">
              <MdLocalShipping aria-hidden="true" />
              <span>
                We deliver in your area on{' '}
                <strong>{listDays(deliveryDays.length > 0 ? deliveryDays : days)}</strong>.
              </span>
            </p>
          )}
        </section>

        {/* Driver instructions */}
        <section className="sun-panel dm-driver">
          <h2 className="sun-card-title">Instructions for our driver</h2>
          <p className="dm-driver-hint">
            Let us know anything that helps, for example where to leave your order.
          </p>
          <textarea
            className="form-control dm-driver-input"
            value={note}
            maxLength={DRIVER_NOTE_MAX}
            rows={3}
            onChange={e => handleNoteChange(e.target.value)}
            placeholder="e.g. Please leave in the porch"
            disabled={editingDisabled}
          />
          {emojiHint && (
            <p className="dm-driver-emoji-hint" role="alert">Sorry, emojis are not allowed.</p>
          )}
          <div className="dm-driver-foot">
            <span className="dm-char-count">{note.length}/{DRIVER_NOTE_MAX}</span>
            <button
              type="button"
              className="sun-btn"
              onClick={() => onSaveDriverNote?.(note)}
              disabled={editingDisabled || !onSaveDriverNote || note === driverNote}
            >
              Save
            </button>
          </div>
        </section>
      </div>
    </>
  )
}

export default DeliveryManagementPanel
